<template>
  <AsyncModal
    :show="show"
    header-text="New billing profile"
    :header-icon="['fas', 'map-marker-alt']"
    :async-function="save"
    confirm-text="Create"
    @close="emit('close')"
    @done="onDone"
  >
    <template #content>
      <div class="tw-mb-8 tw-px-4">
        <AddressForm
          :form="form"
          :errors="errors"
          :supports-vat-id="supportsVatID"
          @updated:form="({ field, value }) => updateFormField(field, value)"
        />
      </div>
    </template>
  </AsyncModal>
</template>

<script lang="ts">
import { useUserStore } from 'gpcore-apikit/store/pinia/user';
import type { CreateBillingProfileResponse } from 'gpcore-grpc/gpcore/api/payment/v1/responses_pb';
import AsyncModal from 'gpcore-uikit/components/new/AsyncModal.vue';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import { defineComponent, watch } from 'vue';
import { useRouter } from 'vue-router';
import AddressForm from './address/AddressForm.vue';
import useBillingProfileCountries from './composables/useBillingProfileCountries';
import useBillingProfileFields from './composables/useBillingProfileFields';

export default defineComponent({
  emits: ['close', 'created'],
  components: {
    AddressForm,
    AsyncModal,
  },
  props: {
    show: {
      default: false,
      type: Boolean,
    },
    redirectAfter: {
      default: false,
      type: Boolean,
    },
  },

  setup(props, { emit }) {
    const { addNotification } = useNotifications();
    const userStore = useUserStore();

    const { countryOptions } = useBillingProfileCountries();

    const { form, supportsVatID, handleSubmit, errors, reset, updateFormField } =
      useBillingProfileFields({
        billingProfile: null,
      });

    const router = useRouter();
    const submit = handleSubmit(() => {
      if (!supportsVatID.value) {
        form.vatId = '';
      }

      return userStore.createBillingProfile({
        ...form,
        countryCode: form.country.key,
      });
    });

    const save = async () => {
      return submit();
    };

    const onDone = (response: CreateBillingProfileResponse) => {
      emit('created', response.billingProfile);
      addNotification({
        message: 'Billing profile has been created.',
        type: 'success',
      });

      if (props.redirectAfter) {
        router.go(-1);
      }
    };

    watch(
      () => props.show,
      () => {
        if (!props.show) {
          reset();
        }
      },
    );

    return {
      countryOptions,
      save,
      onDone,
      emit,
      form,
      errors,
      supportsVatID,
      updateFormField,
    };
  },
});
</script>
